/**
 * "Send me back where I was going" — the deep-link handoff between the auth
 * guard and the login screens.
 *
 * A signed-out user who opens e.g. /webinar/<uuid>/223/<slug> is bounced to
 * /login?returnUrl=%2Fwebinar%2F... and, once signed in, lands on that page
 * rather than on /home.
 *
 * WHY THE SANITISING MATTERS. A login page that redirects to whatever a query
 * parameter says is an open redirect: a link like
 *   https://caira.mileseducation.com/login?returnUrl=https://caira-login.example
 * would show our real, trusted login and then hand the freshly signed-in user
 * to somebody else's page. So a return URL is only ever honoured when it
 * resolves to a path on THIS origin, and the check is done by parsing it rather
 * than by matching strings — "//evil.com" and "/\evil.com" are both off-origin
 * despite looking relative, and the URL parser knows that.
 */

/** Where a signed-in user goes when there is no return URL to honour. */
export const DEFAULT_POST_LOGIN_URL = "/home";

/** The query parameter the guard sets and the login screens read. */
export const RETURN_URL_PARAM = "returnUrl";

/**
 * Screens that must never be a return target.
 *
 * /login would loop. /account-update and /maintenance are gates the user was
 * pushed OUT to; returning to them after a successful login would undo the very
 * thing that let them in.
 */
const NON_RETURNABLE = /^\/(login|account-update|maintenance)(\/|\?|$)/;

/**
 * A same-origin path safe to navigate to, or null.
 *
 * Returns the path + query + hash only — never an absolute URL — so callers can
 * hand it straight to Router.navigateByUrl().
 */
export function sanitizeReturnUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value) {
    return null;
  }

  // Only two shapes are even considered: a rooted path, or a fully-qualified
  // http(s) URL (checked against our origin below). Everything the guard writes
  // is the first kind; a bare "webinars" would resolve harmlessly enough, but
  // narrowing the accepted input is free and leaves less to reason about.
  if (!value.startsWith("/") && !/^https?:\/\//i.test(value)) {
    return null;
  }

  let parsed: URL;
  try {
    // The base makes a relative value resolvable; an absolute value ignores it
    // and keeps its own origin, which is exactly what the check below catches.
    parsed = new URL(value, window.location.origin);
  } catch {
    return null;
  }

  if (parsed.origin !== window.location.origin) {
    return null;
  }

  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  if (!path.startsWith("/") || NON_RETURNABLE.test(path)) {
    return null;
  }
  // Nothing gained by "returning" to the place we already send people.
  if (path === DEFAULT_POST_LOGIN_URL) {
    return null;
  }

  return path;
}

/**
 * The URL to navigate to after a successful login: the sanitised return URL if
 * there is a usable one, otherwise /home.
 */
export function resolvePostLoginUrl(returnUrl: unknown): string {
  return sanitizeReturnUrl(returnUrl) || DEFAULT_POST_LOGIN_URL;
}
